/**
 * The buyer's tickets, reachable forever from the confirmation email (`/tix/<orderId>?t=…`).
 *
 * The signed token in the link is the whole credential — no account, no sign-in. While the
 * order is still pending (a card confirmation racing the webhook), the page polls until the
 * tickets exist, mirroring the donation receipt's webhook-confirmed-truth rule.
 */
import { useEffect, useState } from "react";
import {
  TICKET_INVITE_COPY,
  formatMoney,
  type TicketAppInviteWire,
  type TicketOrderResponse,
} from "@givehere/contracts";
import { fetchTicketOrder } from "./api.js";
import { TicketQr } from "./TicketQr.js";

const POLL_MS = 2_500;
const MAX_POLLS = 40;

export function TicketOrderView({
  orderId,
  accessToken,
}: {
  orderId: string;
  accessToken: string;
}) {
  const [order, setOrder] = useState<TicketOrderResponse | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let polls = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const load = async (): Promise<void> => {
      try {
        const fetched = await fetchTicketOrder(orderId, accessToken);
        if (cancelled) return;
        setOrder(fetched);
        // Keep polling while the money is in flight and the webhook has not issued yet.
        if (fetched.status === "pending" && polls < MAX_POLLS) {
          polls += 1;
          timer = setTimeout(() => void load(), POLL_MS);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [orderId, accessToken]);

  if (failed) {
    return (
      <main className="card">
        <p className="notice">This link is not valid.</p>
      </main>
    );
  }
  if (!order) {
    return (
      <main className="card">
        <p className="muted">Loading your tickets…</p>
      </main>
    );
  }

  return (
    <main className="card">
      <header className="tickets__head">
        <p className="muted">{order.organizationDisplayName}</p>
        <h1 dir="auto">{order.eventName}</h1>
        <p className="muted">
          {order.quantity} × {order.tierName}
          {BigInt(order.amount.amountMinor) > 0n ? ` · ${formatMoney(order.amount)}` : ""}
        </p>
      </header>

      {order.status === "pending" && <p className="notice">Confirming your payment…</p>}
      {order.status === "failed" && (
        <p className="notice notice--error">
          The payment did not complete, so no tickets were issued.
        </p>
      )}
      {order.status === "refunded" && <p className="notice">Refunded — tickets no longer valid.</p>}

      {order.tickets.map((ticket, index) => (
        <section
          key={ticket.id}
          className={ticket.status === "voided" ? "ticket ticket--voided" : "ticket"}
        >
          <p className="ticket__title">
            Ticket {index + 1} of {order.tickets.length}
            {ticket.status === "checked_in" && (
              <span className="badge badge--open">checked in</span>
            )}
            {ticket.status === "voided" && <span className="badge">void</span>}
          </p>
          {ticket.status !== "voided" && <TicketQr code={ticket.code} />}
          <p className="ticket__code">{ticket.code}</p>
        </section>
      ))}

      {order.status === "paid" && (
        <p className="muted">
          These tickets were also emailed to you. Show the QR at the door — a screenshot works.
        </p>
      )}

      {order.appInvites.length > 0 && <AppInvites invites={order.appInvites} />}

      <p className="tickets__notice">{order.admissionNotice}</p>
    </main>
  );
}

/** "November 2, 2026" from the wire's `YYYY-MM-DD` (UTC, as the server and the emails say it). */
const readableDay = (isoDay: string): string =>
  new Date(`${isoDay}T00:00:00Z`).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });

/** Whose an invite is, in the buyer's words (GH-884). */
export function inviteHolderLabel(invite: TicketAppInviteWire): string {
  if (invite.holder === "buyer") return TICKET_INVITE_COPY.holderBuyer;
  if (invite.holder === "guest" && invite.guestEmail) {
    return invite.emailed
      ? TICKET_INVITE_COPY.emailedTo(invite.guestEmail)
      : TICKET_INVITE_COPY.notEmailed(invite.guestEmail);
  }
  return TICKET_INVITE_COPY.holderForward;
}

const STATUS_LABEL: Record<Exclude<TicketAppInviteWire["status"], "open">, string> = {
  redeemed: TICKET_INVITE_COPY.redeemed,
  void: TICKET_INVITE_COPY.voided,
  expired: TICKET_INVITE_COPY.expired,
};

/**
 * GH-884: the GiveHere app invite each ticket came with — the buyer's own, a guest's that was
 * emailed (no code here; it went to them), and the ones to pass on. Server-composed: which codes
 * show, and when they lapse, are the server's answer, never this page's.
 */
function AppInvites({ invites }: { invites: readonly TicketAppInviteWire[] }) {
  return (
    <section className="invites" aria-labelledby="invites-title">
      <h2 id="invites-title" className="tickets__section">
        {TICKET_INVITE_COPY.sectionTitle}
      </h2>
      <ul className="invites__list">
        {invites.map((invite) => (
          <li key={invite.position} className="invites__item">
            <p className="invites__who">
              <span>
                Ticket {invite.position} · {inviteHolderLabel(invite)}
              </span>
              {invite.status !== "open" && (
                <span className="badge badge--muted">{STATUS_LABEL[invite.status]}</span>
              )}
            </p>
            {invite.code && (
              <>
                <p className="invites__code" translate="no">
                  {invite.code}
                </p>
                <p className="invites__until">
                  {TICKET_INVITE_COPY.useBy(readableDay(invite.expiresOn))}
                </p>
              </>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
